import { useEffect } from 'react';
import { clsx } from 'clsx';

export interface ToastProps {
  id: string;
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  duration?: number; // Auto-dismiss duration in ms (0 = no auto-dismiss)
  onClose?: (id: string) => void;
  className?: string;
}

const variantStyles = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-900/95 border-blue-200 dark:border-blue-700',
    title: 'text-blue-800 dark:text-blue-300',
    message: 'text-blue-700 dark:text-blue-200',
    icon: 'text-blue-600 dark:text-blue-400',
    progress: 'bg-blue-500 dark:bg-blue-400',
  },
  success: {
    container: 'bg-green-50 dark:bg-green-900/95 border-green-200 dark:border-green-700',
    title: 'text-green-800 dark:text-green-300',
    message: 'text-green-700 dark:text-green-200',
    icon: 'text-green-600 dark:text-green-400',
    progress: 'bg-green-500 dark:bg-green-400',
  },
  warning: {
    container: 'bg-amber-50 dark:bg-amber-900/95 border-amber-200 dark:border-amber-700',
    title: 'text-amber-800 dark:text-amber-300',
    message: 'text-amber-700 dark:text-amber-200',
    icon: 'text-amber-600 dark:text-amber-400',
    progress: 'bg-amber-500 dark:bg-amber-400',
  },
  error: {
    container: 'bg-red-50 dark:bg-red-900/95 border-red-200 dark:border-red-700',
    title: 'text-red-800 dark:text-red-300',
    message: 'text-red-700 dark:text-red-200',
    icon: 'text-red-600 dark:text-red-400',
    progress: 'bg-red-500 dark:bg-red-400',
  },
};

const defaultIcons = {
  info: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
  success: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  warning: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

export function Toast({
  id,
  variant = 'info',
  title,
  message,
  duration = 0,
  onClose,
  className,
}: ToastProps) {
  const styles = variantStyles[variant];
  const icon = defaultIcons[variant];

  useEffect(() => {
    if (duration > 0 && onClose) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <div
      className={clsx(
        'relative flex w-full max-w-md items-start gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300',
        styles.container,
        className
      )}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {/* Icon */}
      <div className={clsx('flex-shrink-0', styles.icon)}>{icon}</div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {title && <h3 className={clsx('mb-1 text-sm font-semibold', styles.title)}>{title}</h3>}
        <p className={clsx('text-sm', styles.message)}>{message}</p>
      </div>

      {/* Close Button */}
      {onClose && (
        <button
          type="button"
          onClick={() => onClose(id)}
          className={clsx(
            'inline-flex flex-shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors',
            'hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:hover:bg-white/10',
            styles.icon
          )}
          aria-label="Close notification"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}

      {/* Progress Bar (if auto-dismiss) */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-lg bg-gray-200 dark:bg-gray-700">
          <div
            className={clsx('h-full animate-progress', styles.progress)}
            style={{
              animation: `progress ${duration}ms linear`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// Toast Container Component
export interface ToastContainerProps {
  toasts: ToastProps[];
  position?:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top-center'
    | 'bottom-center';
}

const positionStyles = {
  'top-right': 'top-4 right-4 items-end',
  'top-left': 'top-4 left-4 items-start',
  'bottom-right': 'bottom-4 right-4 items-end',
  'bottom-left': 'bottom-4 left-4 items-start',
  'top-center': 'top-4 left-1/2 -translate-x-1/2 items-center',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 items-center',
};

export function ToastContainer({ toasts, position = 'top-right' }: ToastContainerProps) {
  return (
    <div
      className={clsx(
        'pointer-events-none fixed z-50 flex flex-col gap-3',
        positionStyles[position]
      )}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto animate-slide-in">
          <Toast {...toast} />
        </div>
      ))}
    </div>
  );
}
